import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { Link } from "react-router-dom";

import { Button, Card, Header, Icon, Message } from "simplydo/react/ui";

import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import IdeaBusinessProfileModal from "components/lib/IdeaBusinessProfileModal/IdeaBusinessProfileModal";

import InviteeChip from "./InviteeChip";
import type { HomeInvitation, InviteCardProps } from "./types";

const IdeaBusinessProfileInviteCard = ({
  invitation,
  acceptInvite,
  rejectInvite,
  removeInvite,
  cardView = true,
}: InviteCardProps) => {
  const { inviterUser, ideaBusinessProfile, createdByCurrentUser } = invitation;
  const [viewingProfile, setViewingProfile] = useState<
    HomeInvitation["ideaBusinessProfile"] | null
  >(null);
  const user = useAppSelector((state) => state.user);
  const accept = useCallback(() => {
    util
      .confirm(
        "Join business profile",
        `Are you sure you want to join "${ideaBusinessProfile?.name}"?`,
      )
      .then(() => {
        acceptInvite(
          invitation,
          () => {
            toast.success("Joined business profile");
          },
          () => toast.error("Error joining business profile"),
        );
      })
      .catch(() => {});
  }, [acceptInvite, invitation, ideaBusinessProfile?.name]);

  const reject = useCallback(() => {
    util
      .confirm(
        "Reject business profile invitation",
        `Are you sure you want to reject the invitation to join "${ideaBusinessProfile?.name || "Deleted profile"}"?`,
      )
      .then(() => {
        rejectInvite(
          invitation,
          () => {
            toast("Invitation rejected");
          },
          () => toast.error("Error rejecting invite"),
        );
      })
      .catch(() => {});
  }, [rejectInvite, invitation, ideaBusinessProfile?.name]);

  const remove = useCallback(() => {
    util
      .confirm(
        "Remove business profile invitation",
        `Are you sure you want to remove this invitation to join "${ideaBusinessProfile?.name || "Deleted business profile"}"?`,
      )
      .then(() => {
        removeInvite(
          invitation,
          () => {
            toast("Invitation removed");
          },
          () => toast.error("Error removing invite"),
        );
      })
      .catch(() => {});
  }, [removeInvite, invitation, ideaBusinessProfile?.name]);

  const canAccept = invitation.canAccept && !!ideaBusinessProfile;

  return (
    <div
      style={
        cardView ?
          {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }
        : {}
      }
    >
      {viewingProfile ?
        <IdeaBusinessProfileModal
          businessProfile={viewingProfile}
          onClose={() => setViewingProfile(null)}
        />
      : null}
      <div>
        {cardView ?
          <Header
            style={{ marginBottom: "0.3em" }}
            as={Link}
            disabled={!ideaBusinessProfile}
            onClick={() =>
              ideaBusinessProfile && setViewingProfile(ideaBusinessProfile)
            }
          >
            {ideaBusinessProfile?.name || "Deleted profile"}
          </Header>
        : <Header as="h2" icon textAlign="center">
            <Icon name="building" circular />
            <Header.Content>
              Join {ideaBusinessProfile?.name || `Deleted profile`}
            </Header.Content>
          </Header>
        }
        <Card.Description>
          {createdByCurrentUser ?
            <InviteeChip
              invitation={invitation}
              preText="You invited"
              postText="to join this business profile"
            />
          : <UserChip
              compact="very"
              user={inviterUser}
              postText="has invited you to join this business profile"
            />
          }
        </Card.Description>
      </div>
      <Card.Content extra>
        <div
          className="ui"
          style={
            cardView ?
              {}
            : { marginTop: 20, display: "flex", justifyContent: "flex-end" }
          }
        >
          {invitation.acceptedAt && ideaBusinessProfile && (
            <Button
              basic
              color="green"
              as={Link}
              disabled={!ideaBusinessProfile}
              onClick={() =>
                ideaBusinessProfile && setViewingProfile(ideaBusinessProfile)
              }
            >
              View business profile
            </Button>
          )}
          {!invitation.acceptedAt && (
            <>
              {!ideaBusinessProfile && (
                <Message error style={{ width: "100%" }}>
                  This invitation to join is no longer valid because the
                  business profile has been deleted.
                </Message>
              )}
              {user && invitation.canRemove ?
                <Button
                  basic
                  color={createdByCurrentUser ? undefined : "red"}
                  onClick={createdByCurrentUser ? remove : reject}
                >
                  {createdByCurrentUser ? "Remove" : "Reject"}
                </Button>
              : null}
              {user && canAccept ?
                <Button basic color="green" onClick={accept}>
                  Accept
                </Button>
              : null}
            </>
          )}
        </div>
      </Card.Content>
    </div>
  );
};

export default IdeaBusinessProfileInviteCard;
